import { TicketAudience, TICKET_AUDIENCE_LABELS } from '@21k/shared';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createIntakeRoute,
  getApiErrorMessage,
  getIntakeRoute,
  listChannelTypeOptions,
  listDepartments,
  updateIntakeRoute,
} from '../../api/client';
import type { ChannelTypeOption, DepartmentSummary } from '../../api/types';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

const AUDIENCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any audience' },
  ...Object.values(TicketAudience).map((a) => ({
    value: a,
    label: TICKET_AUDIENCE_LABELS[a],
  })),
];

export default function IntakeRouteFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [channelTypes, setChannelTypes] = useState<ChannelTypeOption[]>([]);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [name, setName] = useState('');
  const [channelTypeId, setChannelTypeId] = useState('');
  const [audience, setAudience] = useState('');
  const [primaryDepartmentId, setPrimaryDepartmentId] = useState('');
  const [erpSchoolId, setErpSchoolId] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit =
    user && (isNew ? canWritePlatformConfig(user) : canEditPlatformConfig(user));

  useEffect(() => {
    Promise.all([listChannelTypeOptions(), listDepartments()])
      .then(([types, depts]) => {
        setChannelTypes(types);
        setDepartments(depts);
        if (isNew) {
          setChannelTypeId(types[0]?.id ?? '');
          setPrimaryDepartmentId(depts[0]?.id ?? '');
        }
      })
      .catch((err) => setError(getApiErrorMessage(err)));
  }, [isNew]);

  useEffect(() => {
    if (isNew || !id) return;
    getIntakeRoute(id)
      .then((route) => {
        setName(route.name ?? '');
        setChannelTypeId(route.channelTypeId);
        setAudience(route.audience ?? '');
        setPrimaryDepartmentId(route.primaryDepartmentId);
        setErpSchoolId(route.erpSchoolId ?? '');
        setSortOrder(route.sortOrder);
        setIsActive(route.isActive);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    const payload = {
      name: name.trim() || undefined,
      channelTypeId,
      audience: audience ? (audience as TicketAudience) : null,
      primaryDepartmentId,
      erpSchoolId: erpSchoolId.trim() || null,
      sortOrder,
      isActive,
    };
    try {
      if (isNew) {
        await createIntakeRoute(payload);
        navigate(PLATFORM_CONFIG.routing);
      } else if (id) {
        await updateIntakeRoute(id, payload);
        navigate(PLATFORM_CONFIG.routing);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) return <div className="alert alert-error">No permission.</div>;
  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <Link to={PLATFORM_CONFIG.routing} style={{ fontSize: '0.85rem' }}>
          ← Routing
        </Link>
        <h1>{isNew ? 'New intake route' : 'Edit intake route'}</h1>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <form className="card card-body" style={{ maxWidth: 640 }} onSubmit={handleSave}>
        <div className="form-group">
          <label>Name</label>
          <input
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Parenthub → CX"
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Channel type</label>
            <select
              className="form-control"
              value={channelTypeId}
              onChange={(e) => setChannelTypeId(e.target.value)}
              required
            >
              {channelTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Audience</label>
            <select
              className="form-control"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
            >
              {AUDIENCE_OPTIONS.map((opt) => (
                <option key={opt.value || 'any'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Primary department</label>
          <select
            className="form-control"
            value={primaryDepartmentId}
            onChange={(e) => setPrimaryDepartmentId(e.target.value)}
            required
          >
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.type})
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>ERP school ID (optional)</label>
          <input
            className="form-control"
            value={erpSchoolId}
            onChange={(e) => setErpSchoolId(e.target.value)}
            placeholder="Match tickets for a specific school"
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Sort order</label>
            <input
              className="form-control"
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />{' '}
              Active
            </label>
          </div>
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create route' : 'Save changes'}
        </button>
      </form>
    </>
  );
}
